import React from 'react';
import { ChevronRight } from 'lucide-react';
import type { CfbSchoolCatalogEntry } from '../cfbSchoolCatalogTypes';
import { resolveSchoolAccentColor } from '../cfbBranding/cfbSchoolAccent';
import TeamVisualChip from './leagueHome/TeamVisualChip';

function formatConferencePill(conferenceKey: string): string {
  const normalized = conferenceKey.trim();
  if (!normalized) {
    return '—';
  }
  return normalized.toUpperCase();
}

export type CfbSchoolListRowProps = {
  school: CfbSchoolCatalogEntry;
  showChevron?: boolean;
  className?: string;
  onClick?: () => void;
  as?: 'button' | 'div';
  selected?: boolean;
};

const CfbSchoolListRow: React.FC<CfbSchoolListRowProps> = ({
  school,
  showChevron = false,
  className = '',
  onClick,
  as = 'div',
  selected = false,
}) => {
  const accent = resolveSchoolAccentColor(school);

  const content = (
    <>
      <span
        className="absolute inset-y-0 left-0 w-1 rounded-l-md"
        style={{ backgroundColor: accent.accentColor }}
        aria-hidden="true"
        data-testid="cfb-school-row-accent-bar"
      />
      <TeamVisualChip abbreviation={school.initials} accentColor={accent.accentColor} size="sm" />
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-semibold text-white">{school.athletic_name}</span>
          <span
            className="inline-flex shrink-0 rounded-full border border-secondary-600 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-secondary-300"
            data-testid="cfb-school-conference-pill"
          >
            {formatConferencePill(school.conference_key)}
          </span>
        </span>
        <span className="mt-0.5 block truncate text-xs text-secondary-400">{school.conference_name}</span>
      </span>
      {showChevron ? (
        <ChevronRight className="h-4 w-4 shrink-0 text-secondary-500" aria-hidden="true" />
      ) : null}
    </>
  );

  const rowClass = [
    'relative flex w-full items-center gap-3 py-2.5 pl-3 pr-3 text-left',
    selected ? 'bg-secondary-900/80' : 'hover:bg-secondary-900/60',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (as === 'button') {
    return (
      <button
        type="button"
        className={rowClass}
        onClick={onClick}
        data-testid={`cfb-school-row-${school.school_id}`}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={rowClass} data-testid={`cfb-school-row-${school.school_id}`}>
      {content}
    </div>
  );
};

export default CfbSchoolListRow;
